import { CommonModule } from '@angular/common';
import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DesignPartnerDomainsService } from '../../../services/design-partner-domains.service';
import { ToastService } from '../../../services/toast.service';
import { SubscriptionService } from '../../../services/subscription.service';

@Component({
  selector: 'app-domain-subdomains',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './domain-subdomains.component.html',
  styleUrl: './domain-subdomains.component.less',
})
export class DomainSubdomainsComponent implements OnInit {
  domain: string = '';
  records: any[] = [];
  isLoading: boolean = false;
  noRecordsMessage: string = 'No certificate records found for this domain.';

  currentPage: number = 1;
  readonly pageSize: number = 10;

  selectedRecord: any = null;
  openMenuIndex: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private domainsService: DesignPartnerDomainsService,
    private toaster: ToastService,
    private subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.domain = this.route.snapshot.paramMap.get('domain') ?? '';
    if (!this.domain) {
      this.router.navigate(['/domains']);
      return;
    }
    this.loadRecords();
  }

  loadRecords(): void {
    this.isLoading = true;
    this.domainsService.getSubDomains(this.domain).subscribe({
      next: (response: any) => {
        const raw = response?.records ?? response?.data ?? response ?? [];
        this.records = Array.isArray(raw) ? raw : [];
        this.isLoading = false;
        if (this.records.length === 0) {
          this.noRecordsMessage = 'No certificate records have been saved for this domain yet.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.noRecordsMessage = 'Failed to load records. Please try again later.';
        this.toaster.showToast('Failed to load certificate records', 'error');
      },
    });
  }

  get totalPages(): number {
    return Math.ceil(this.records.length / this.pageSize);
  }

  get paginatedRecords(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.records.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.openMenuIndex = null;
    }
  }

  getDaysRemaining(notAfter: string): number {
    return Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  getDaysClass(notAfter: string): string {
    const days = this.getDaysRemaining(notAfter);
    if (days < 0) return 'days-expired';
    if (days <= 30) return 'days-critical';
    if (days <= 90) return 'days-warning';
    return 'days-ok';
  }

  getRiskClass(riskLevel: string): string {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL': return 'risk-critical';
      case 'HIGH':     return 'risk-high';
      case 'MEDIUM':   return 'risk-medium';
      default:         return 'risk-low';
    }
  }

  getTlsClass(supported: boolean): string {
    return supported ? 'tls-supported' : 'tls-unsupported';
  }

  openPopup(cert: any): void {
    this.selectedRecord = cert;
  }

  closePopup(): void {
    this.selectedRecord = null;
  }

  toggleMenu(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  isExpired(cert: any): boolean {
    return this.getDaysRemaining(cert.notAfter) < 0;
  }

  isSslInstalled(cert: any): boolean {
    return cert.tls && cert.tls.length > 0;
  }

  renewCertificate(cert: any): void {
    this.openMenuIndex = null;
    const domain = cert.commonName || this.domain;
    if (this.subscriptionService.hasActiveSubscription()) {
      this.router.navigate(['/chat'], {
        state: { initialPrompt: `Hi, I want to install SSL certificate for ${domain}` },
      });
    } else {
      this.router.navigate(['/subscription'], { queryParams: { url: 'chat' } });
    }
  }

  installCertificate(cert: any): void {
    this.openMenuIndex = null;
    const domain = cert.commonName || this.domain;
    if (this.subscriptionService.hasActiveSubscription()) {
      this.router.navigate(['/chat'], {
        state: { initialPrompt: `Hi, I want to install SSL certificate for ${domain}` },
      });
    } else {
      this.router.navigate(['/subscription'], { queryParams: { url: 'chat' } });
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openMenuIndex = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closePopup();
    this.openMenuIndex = null;
  }

  goBack(): void {
    this.router.navigate(['/domains']);
  }
}
